import React, { useEffect, useState } from 'react';

import { css, cssMap } from '@atlaskit/css';
import LinkIcon from '@atlaskit/icon/core/link';
import { token } from '@atlaskit/tokens';

import type { SmartLinkIcon } from './utils/smartLinkTypes.ts';

const baseStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
});

// Image icons fill their wrapper; the wrapper carries the desired pixel size.
const imgStyles = css({
	display: 'block',
	width: '100%',
	height: '100%',
	objectFit: 'contain',
});

// Image icons (<img>) scale to fill their wrapper natively (width/height 100% + object-fit:
// contain, set on the <img> in ImageIcon below), so we just size the wrapper to the desired size.
const imageStyles = cssMap({
	stack: {
		width: '10px',
		height: '10px',
	},
	menu: {
		width: '16px',
		height: '16px',
	},
});

// AtlasKit React-element icons (@atlaskit/icon, @atlaskit/object) render an <svg> at a fixed
// intrinsic 16px that ignores the wrapper size, so we render at that natural size and scale the
// whole composed icon down with a centred transform — this shrinks any element icon uniformly
// without distorting or clipping it. 16px -> 10px in the stack; the menu keeps the natural size.
const elementStyles = cssMap({
	stack: {
		width: '16px',
		height: '16px',
		// The element icon's <svg> sits slightly high in its box due to its inline baseline; a small
		// top padding nudges it back to centre. Tune this token if it over/under-corrects.
		paddingBlockStart: token('space.025'),
		transform: 'scale(0.625)',
		transformOrigin: 'center',
	},
	menu: {
		width: '16px',
		height: '16px',
	},
});

export type SmartLinkIconRendererProps = {
	icon: SmartLinkIcon;
	size: 'stack' | 'menu';
};

type ImageIconProps = {
	src: string;
	size: 'stack' | 'menu';
};

// Remote icon images can fail to load (404, network error, expired/blocked URL), which otherwise
// surfaces as the browser's "broken image" glyph. <img> load failures aren't caught by React error
// boundaries — they only surface through the DOM `onError` event — so we attach `onError` to the
// <img> at the point it's created here and swap in a neutral link icon on failure.
const ImageIcon = ({ src, size }: ImageIconProps) => {
	const [hasErrored, setHasErrored] = useState(false);

	// Reset when the source changes — the menu reuses one renderer instance across rows.
	useEffect(() => {
		setHasErrored(false);
	}, [src]);

	if (hasErrored) {
		// The fallback is an <svg> element icon, so it uses the element wrapper (which applies the
		// stack scale-down / menu natural-size logic) rather than the image wrapper.
		return (
			<span css={[baseStyles, elementStyles[size]]} aria-hidden="true">
				<LinkIcon label="" color={token('color.icon.subtle')} />
			</span>
		);
	}

	return (
		<span css={[baseStyles, imageStyles[size]]} aria-hidden="true">
			<img alt="" src={src} css={imgStyles} onError={() => setHasErrored(true)} />
		</span>
	);
};

export const SmartLinkIconRenderer = ({
	icon,
	size,
}: SmartLinkIconRendererProps): React.JSX.Element =>
	icon.kind === 'image' ? (
		<ImageIcon src={icon.src} size={size} />
	) : (
		<span css={[baseStyles, elementStyles[size]]} aria-hidden="true">
			{icon.node}
		</span>
	);
